import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Breadcrumb, Button, Badge } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import {
  useGetClientGroupByIdQuery,
  useGetClientsInGroupQuery,
  useAssignClientToGroupMutation,
  useUnassignClientFromGroupMutation,
} from "@/api/clientGroupApi/clientGroup";
import ClientGroupClientsTable from "@/components/clientGroups/ClientGroupClientsTable";
import ClientAssignmentDialog from "@/components/clientGroups/ClientAssignmentDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import LoadingPage from "@/components/LoadingPage";
import { getErrorMessage } from "@/utils/requestUtils";
import { DataGridPagination } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function ClientGroupDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["clientGroup", "common"]);
  const user = useCurrentUser();
  const isAdmin = user?.agent_role === agentRoles.company_admin;
  const [page, setPage] = useState(1);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    body: string;
    onConfirm: () => void;
  } | null>(null);

  const { data: group, isLoading: isGroupLoading } =
    useGetClientGroupByIdQuery(id as string);

  const { data: clientsData, isLoading: isClientsLoading } =
    useGetClientsInGroupQuery(
      {
        clientGroupId: parseInt(id as string),
        params: {
          pagination: { page, pageSize: 15 },
          filters: {},
        },
      },
      { skip: !id }
    );

  const [assignClients] = useAssignClientToGroupMutation();
  const [unassignClient] = useUnassignClientFromGroupMutation();

  const handleAssignClients = (clientIds: number[]) => {
    if (!id) return;
    assignClients({
      clientGroupId: parseInt(id),
      client_ids: clientIds,
    })
      .unwrap()
      .then(() => {
        toast.success(t("clientGroup:assignSuccess"));
        setIsAssignModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err) || t("clientGroup:assignFail")));
  };

  const handleRemoveClient = (clientId: number) => {
    if (!id) return;
    setConfirmDialog({
      title: t("clientGroup:removeClientTitle"),
      body: t("clientGroup:removeClientBody"),
      onConfirm: () => {
        unassignClient({ clientGroupId: parseInt(id), client_ids: [clientId] })
          .unwrap()
          .then(() => {
            toast.success(t("clientGroup:removeClientSuccess"));
            setConfirmDialog(null);
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const clients = clientsData?.items || clientsData?.data || [];
  const paginationData = clientsData?.pagination || {};

  const pagination: DataGridPagination = {
    pageIndex: paginationData.pageIndex || 1,
    pageSize: paginationData.pageSize || 15,
    totalPages: paginationData.totalPages || 1,
    totalItems: paginationData.totalItems || clients.length,
  };

  return (
    <NavbarSidebarLayout>
      {isGroupLoading ? (
        <LoadingPage />
      ) : (
        <>
          <div className="p-4 bg-white block sm:flex items-center justify-between border-b border-gray-200 lg:mt-1.5 dark:bg-gray-800 dark:border-gray-700">
            <div className="w-full mb-1">
              <div className="mb-4">
                <Breadcrumb className="mb-4">
                  <Breadcrumb.Item>
                    <Link to="/" className="flex items-center gap-x-3">
                      <HiHome className="text-xl" />
                      <span className="dark:text-white">
                        {t("common:home")}
                      </span>
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    <Link to="/client-groups">
                      {t("clientGroup:title")}
                    </Link>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>
                    {group?.group_name || t("clientGroup:groupDetails")}
                  </Breadcrumb.Item>
                </Breadcrumb>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {group?.group_name
                    ? `${group.group_name} ${t("clientGroup:groupDetails")}`
                    : t("clientGroup:groupDetails")}
                </h1>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t("clientGroup:groupInformation")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {t("clientGroup:groupName")}:
                  </span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {group?.group_name}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {t("clientGroup:description")}:
                  </span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {group?.group_description || "-"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {t("clientGroup:clientCount")}:
                  </span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {group?.client_count ?? 0}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {t("clientGroup:status")}:
                  </span>
                  <Badge
                    color={group?.is_active ? "success" : "failure"}
                    className="ml-2 inline"
                  >
                    {group?.is_active
                      ? t("clientGroup:active")
                      : t("clientGroup:inactive")}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("clientGroup:assignedClients")}
                </h3>
                {isAdmin && (
                  <Button
                    color="primary"
                    onClick={() => setIsAssignModalOpen(true)}
                  >
                    {t("clientGroup:assignClients")}
                  </Button>
                )}
              </div>
              <ClientGroupClientsTable
                data={clients}
                pagination={pagination}
                onPageChange={setPage}
                loading={isClientsLoading}
                onRemoveClient={handleRemoveClient}
              />
            </div>
          </div>
        </>
      )}

      <ClientAssignmentDialog
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSubmit={handleAssignClients}
      />
      {confirmDialog && (
        <ConfirmationDialog
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          body={confirmDialog.body}
        />
      )}
    </NavbarSidebarLayout>
  );
}
